// 新建 / 编辑任务：选类型 → 客户 → manifest 表单 → 材料（先建草稿再上传）→ 备注 → 优先级 → 保存 / 保存并开始
// 修改已有任务时带上读到的 updatedAt（expectedUpdatedAt），别人先改过会 409 → toast「数据已更新，请刷新后重试」+ 刷新
import { PersonSelect, useCorppassPeople } from "../components/AssigneePicker";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Priority, TaskDTO, TaskTypeDTO } from "@csp/shared";
import { applyFileFlags, resolveRequired } from "@csp/shared";
import { api, CONFLICT_TEXT, errorText, isStaleConflict } from "../lib/api";
import { useAlerts } from "../lib/alerts";
import { useAuth } from "../lib/auth";
import { PRIORITY_TEXT } from "../lib/format";
import { useLoad } from "../lib/hooks";
import { initialValues, missingFiles, toManifest, validateAll, type FieldValue, type FormValues } from "../lib/manifest";
import { uploadTaskFile } from "../lib/upload";
import { ManifestForm } from "../components/ManifestForm";
import { MaterialsPicker, type UploadProgress } from "../components/Materials";
import { MaturityBadge } from "../components/Badges";
import { PageHeading } from "../components/PageHeading";

const UEN_RE = /^[0-9A-Z]{9,10}$/;

export function NewTaskPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const types = useLoad(() => api.types(), []);
  const existing = useLoad<TaskDTO | null>(() => (id ? api.task(id) : Promise.resolve(null)), [id]);
  const [typeId, setTypeId] = useState<string | null>(null);

  useEffect(() => {
    if (existing.data) setTypeId(existing.data.typeId);
  }, [existing.data]);

  if (types.error) return <div className="notice err">{types.error}</div>;
  if (!types.data || (editing && !existing.data)) return <div className="empty">{existing.error ?? "加载中…"}</div>;

  const enabled = types.data.filter((t) => t.enabled);
  const type: TaskTypeDTO | undefined = types.data.find((t) => t.id === typeId);

  // 版本冲突后「刷新」：编辑页重新拉任务（表单按新的 updatedAt 重建）；新建页已建出草稿的跳到它的编辑页
  const refresh = (taskId: string) => {
    if (editing) void existing.reload();
    else navigate(`/tasks/${taskId}/edit`, { replace: true });
  };

  if (!type && editing && existing.data) {
    return <TypeLoader typeId={existing.data.typeId} task={existing.data} onRefresh={refresh} />;
  }
  if (!type) {
    return (
      <div>
        <PageHeading title="新建任务" desc="第 1 步：选任务类型。只列出已启用的类型，卡片上写了大约要跑多久、要准备几项材料" />
        {enabled.length === 0 && (
          <div className="card">
            <div className="empty">还没有启用的任务类型，请联系管理员</div>
          </div>
        )}
        <div className="grid grid-3">
          {enabled.map((t) => (
            <button key={t.id} className="card type-pick" onClick={() => setTypeId(t.id)} data-testid="type-pick" data-slug={t.slug}>
              <h2>{t.name}</h2>
              <p className="muted small" style={{ margin: "4px 0 10px" }}>{t.description || "（无说明）"}</p>
              <div className="row small">
                <MaturityBadge maturity={t.maturity} />
                <span className="muted">约 {t.runtime.expectedMinutes} 分钟</span>
                {t.manifestSchema.files.length > 0 && <span className="muted">需材料 {t.manifestSchema.files.length} 项</span>}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }
  // 新建：先起名，建出草稿后直接进全屏「准备文件」；长表单只留给编辑
  if (!editing) return <NameStep type={type} onBack={() => setTypeId(null)} />;
  return (
    <TaskForm
      key={type.id + (id ?? "") + (existing.data?.updatedAt ?? "")}
      type={type}
      task={existing.data}
      onBack={editing ? null : () => setTypeId(null)}
      onRefresh={refresh}
    />
  );
}

/** 编辑时类型未启用（不在 enabled 列表）也要能编辑：单独拉类型 */
function TypeLoader({ typeId, task, onRefresh }: { typeId: string; task: TaskDTO; onRefresh: (taskId: string) => void }) {
  const t = useLoad(() => api.type(typeId), [typeId]);
  if (t.error) return <div className="notice err">{t.error}</div>;
  if (!t.data) return <div className="empty">加载中…</div>;
  return <TaskForm key={task.updatedAt} type={t.data} task={task} onBack={null} onRefresh={onRefresh} />;
}

function TaskForm({
  type,
  task,
  onBack,
  onRefresh,
}: {
  type: TaskTypeDTO;
  task: TaskDTO | null;
  onBack: (() => void) | null;
  onRefresh: (taskId: string) => void;
}) {
  const { user } = useAuth();
  const { conflict } = useAlerts();
  const navigate = useNavigate();
  const baseSchema = type.manifestSchema;
  const [current, setCurrent] = useState<TaskDTO | null>(task);
  const [name, setName] = useState(task?.clientEntity.name ?? "");
  const [uen, setUen] = useState(task?.clientEntity.uen ?? "");
  const [values, setValues] = useState<FormValues>(() => initialValues(baseSchema, task?.manifest ?? {}));
  // 条件必填按表单当前值算（如「担保人是否在职」选是 → 担保人在职证明变必备）
  // 由文件决定的字段（如 担保人是否在职 ← 担保人在职证明）不在表单里填，按选了 / 传了的文件算
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const fullSchema = useMemo(() => resolveRequired(baseSchema, applyFileFlags(baseSchema, values, selectedKeys)), [baseSchema, values, selectedKeys]);
  const schema = useMemo(() => ({ ...fullSchema, fields: fullSchema.fields.filter((f) => !f.fromFile) }), [fullSchema]);
  const [note, setNote] = useState(task?.materialsNote ?? "");
  const [priority, setPriority] = useState<Priority>(task?.priority ?? "normal");
  // Corppass 类型：选谁来扫码登录（用他的 Chrome）
  const needPerson = type.browserKind === "corppass";
  const { people } = useCorppassPeople();
  const [assignee, setAssignee] = useState<string | null>(task?.assignee ?? null);
  const [selected, setSelected] = useState<Record<string, File[]>>({});
  useEffect(() => {
    setSelectedKeys([...new Set([...(current?.files ?? []).filter((f) => f.state === "synced").map((f) => f.key), ...Object.keys(selected).filter((k) => selected[k]!.length)])]);
  }, [current, selected]);
  const [progress, setProgress] = useState<Record<string, UploadProgress[]>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const errors = useMemo(() => validateAll(schema, values), [schema, values]);
  const uenErr = uen.trim() && !UEN_RE.test(uen.trim().toUpperCase()) ? "UEN 一般是 9–10 位数字/大写字母" : null;
  const fileMissing = missingFiles(schema, current?.files ?? [], selected);
  const canUrgent = user?.role === "boss" || user?.role === "admin";
  const priorities: Priority[] = canUrgent ? ["low", "normal", "high", "urgent"] : ["low", "normal", "high"];

  const setField = (k: string, v: FieldValue) => setValues((s) => ({ ...s, [k]: v }));

  const uploadAll = async (taskId: string): Promise<boolean> => {
    let ok = true;
    for (const [key, files] of Object.entries(selected)) {
      for (const file of files) {
        const upd = (p: UploadProgress) =>
          setProgress((s) => ({ ...s, [key]: [...(s[key] ?? []).filter((x) => x.name !== file.name), p] }));
        upd({ name: file.name, pct: 0, state: "uploading" });
        try {
          await uploadTaskFile(taskId, key, file, (l, t) => upd({ name: file.name, pct: Math.round((l / t) * 100), state: "uploading" }));
          upd({ name: file.name, pct: 100, state: "done" });
        } catch (e) {
          ok = false;
          upd({ name: file.name, pct: 0, state: "error", error: errorText(e) });
        }
      }
    }
    if (ok) setSelected({});
    return ok;
  };

  const save = async (start: boolean) => {
    setSubmitted(true);
    setError(null);
    if (start && (Object.keys(errors).length || fileMissing.length || !name.trim() || uenErr || (needPerson && !assignee))) {
      setError("还有内容没填好（见红字），填好后再开始。也可以先点「保存」存为草稿。");
      return;
    }
    if (!name.trim() && !uen.trim()) {
      setError("请至少填写客户名称或 UEN");
      return;
    }
    setBusy(start ? "start" : "save");
    try {
      const body = {
        clientEntity: { name: name.trim(), uen: uen.trim().toUpperCase() },
        priority,
        manifest: toManifest(schema, values),
        materialsNote: note,
        ...(needPerson ? { assignee } : {}),
      };
      let t: TaskDTO;
      if (current) t = await api.updateTask(current.id, { ...body, expectedUpdatedAt: current.updatedAt });
      else t = await api.createTask({ typeId: type.id, ...body, start: false });
      setCurrent(t);
      const uploaded = await uploadAll(t.id);
      t = await api.task(t.id);
      setCurrent(t);
      if (!uploaded) {
        setError("有文件没传上去，请重新选择后再保存。任务已存为草稿。");
        if (!task) navigate(`/tasks/${t.id}/edit`, { replace: true });
        return;
      }
      if (start) {
        if (t.status !== "ready") {
          setError(`还不能开始：缺 ${t.missing.join("、")}`);
          if (!task) navigate(`/tasks/${t.id}/edit`, { replace: true });
          return;
        }
        await api.startTask(t.id);
      }
      navigate(`/tasks/${t.id}`);
    } catch (e) {
      if (isStaleConflict(e) && current) {
        const taskId = current.id;
        setError(`${CONFLICT_TEXT}。刷新会丢掉这页还没保存的修改。`);
        conflict(() => onRefresh(taskId));
      } else setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHeading
        title={`${task ? `编辑任务 #${task.no}` : "新建任务"} · ${type.name}`}
        desc={task ? "改完点「保存」；资料和材料都齐了才能开始" : "第 2 步：填客户、资料和材料。可以先「保存」存成草稿，材料齐了再开始"}
      >
        {onBack && (
          <button className="btn" onClick={onBack}>
            ← 换个类型
          </button>
        )}
        {task && (
          <Link className="btn" to={`/tasks/${task.id}`}>
            返回任务
          </Link>
        )}
      </PageHeading>
      {current && current.missing.length > 0 && (
        <div className="notice warn" data-testid="missing">
          还缺：{current.missing.join("、")}
        </div>
      )}
      <div className="card">
        <h2>客户</h2>
        <div className="form-grid">
          <label className="field">
            <span className="lbl">
              公司名称<span className="req">*</span>
            </span>
            <input name="clientName" value={name} onChange={(e) => setName(e.target.value)} className={submitted && !name.trim() ? "invalid" : ""} />
            <span className="hint">照 ACRA 上的英文全称填写，如 ACME TRADING PTE. LTD.</span>
          </label>
          <label className="field">
            <span className="lbl">UEN</span>
            <input name="uen" value={uen} onChange={(e) => setUen(e.target.value)} className={uenErr ? "invalid" : ""} />
            {uenErr ? <span className="field-err">{uenErr}</span> : <span className="hint">如 202012345A</span>}
          </label>
        </div>
      </div>
      {needPerson && (
        <div className="card">
          <h2>谁来扫码登录 Corppass</h2>
          <div className="row">
            <PersonSelect value={assignee} onChange={setAssignee} people={people} />
            {submitted && !assignee ? <span className="field-err">请选一个人</span> : <span className="hint">每个人有自己的 Chrome；出二维码时会提示这个人扫</span>}
          </div>
        </div>
      )}
      <div className="card">
        <h2>任务资料</h2>
        <ManifestForm
          schema={schema}
          values={values}
          errors={errors}
          showErrors={submitted ? true : touched}
          onChange={setField}
          onBlur={(k) => setTouched((s) => ({ ...s, [k]: true }))}
        />
      </div>
      <div className="card">
        <h2>材料</h2>
        <MaterialsPicker
          reqs={schema.files}
          selected={selected}
          uploaded={current?.files ?? []}
          onSelect={(k, f) => setSelected((s) => ({ ...s, [k]: f }))}
          progress={progress}
          showMissing={submitted}
        />
        <label className="field">
          <span className="lbl">材料备注</span>
          <textarea name="materialsNote" value={note} onChange={(e) => setNote(e.target.value)} placeholder="例如：客户另外邮件发来的说明、网盘链接等" />
        </label>
      </div>
      <div className="card">
        <h2>优先级</h2>
        <div className="row">
          {priorities.map((p) => (
            <label key={p} className="check" style={{ marginRight: 16 }}>
              <input type="radio" name="priority" checked={priority === p} onChange={() => setPriority(p)} />
              <span className={p === "urgent" ? "err" : ""}>{PRIORITY_TEXT[p]}{p === "urgent" ? "（插队）" : ""}</span>
            </label>
          ))}
        </div>
      </div>
      {error && <div className="notice err" role="alert">{error}</div>}
      <div className="row end" style={{ marginBottom: 40 }}>
        {busy && <span className="muted small">正在保存，请稍候…</span>}
        <button className="btn btn-big" disabled={!!busy} onClick={() => void save(false)}>
          {busy === "save" ? "保存中…" : "保存"}
        </button>
        <button className="btn btn-primary btn-big" disabled={!!busy} onClick={() => void save(true)}>
          {busy === "start" ? "处理中…" : "保存并开始"}
        </button>
      </div>
    </div>
  );
}

/** 新建第 2 步：给任务起名（测试任务也要），然后去准备文件 */
function NameStep({ type, onBack }: { type: TaskTypeDTO; onBack: () => void }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    if (!title.trim()) { setError("请先给任务起个名字"); return; }
    setBusy(true); setError(null);
    try {
      const t = await api.createTask({ typeId: type.id, title: title.trim(), clientEntity: { name: "", uen: "" }, priority: "normal", manifest: {}, materialsNote: "", start: false });
      navigate(`/tasks/${t.id}/prepare`);
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeading title={`新建任务 · ${type.name}`} desc="第 2 步：给任务起个名字。下一步进入全屏页面准备文件，文件和字段都齐了才能开始">
        <button className="btn" onClick={onBack}>← 换个类型</button>
      </PageHeading>
      <div className="card">
        <label className="field">
          <span className="lbl">任务名称<span className="req">*</span></span>
          <input name="title" autoFocus value={title} maxLength={200} placeholder="例如：郭书涛 ICA 入境申诉（第 3 次）" onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void create(); }} />
        </label>
        {error && <div className="notice err" role="alert">{error}</div>}
        <div className="row end">
          <button className="btn btn-primary btn-big" disabled={busy} onClick={() => void create()}>{busy ? "创建中…" : "下一步：准备文件 →"}</button>
        </div>
      </div>
    </div>
  );
}
